import { IonContent, IonPage, IonSpinner, useIonRouter } from "@ionic/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { ArrowRightLeft } from "lucide-react";
import { SignedQueryImage } from "@/components/molecules/signed-query-image";
import { api } from "@/lib/backend";
import { useMe } from "@/lib/hooks/use-me";
import { useMyOrganizations } from "@/lib/query/organization/use-my-organizations";
import { queryOptions_signed_key_public_by_slug } from "@/lib/requests/file/file.get-signed-key";
import { Routes } from "@/lib/router";
import { logger } from "@/logger";

/**
 * Organization Switching Screen
 *
 * Shows a nice transition UI when switching organizations via push notifications.
 *
 * Query params:
 *
 * - TargetOrgId: The organization ID to switch to
 * - Callback: The URL to navigate to after switching (optional)
 */
export default function SwitchOrganization() {
  const { t } = useTranslation();
  const ionRouter = useIonRouter();
  const history = useHistory();
  const { me, sessionProvider } = useMe();
  const { data: organizationsData } = useMyOrganizations();
  const [error, setError] = useState<string | null>(null);

  // Store query params in ref so they stay defined during reload
  const queryParamsRef = useRef<{ targetOrgId: string | null; callback: string | null }>({
    targetOrgId: null,
    callback: null,
  });

  useEffect(() => {
    logger.info(
      {
        ionRouterSearch: ionRouter.routeInfo.search,
        pathname: ionRouter.routeInfo.pathname,
        windowSearch: window.location.search,
      },
      "Switch org route mounted",
    );

    const searchParams = new URLSearchParams(ionRouter.routeInfo.search);
    const targetOrgId = searchParams.get("targetOrgId");
    const callback = searchParams.get("callback");

    // Store in ref
    queryParamsRef.current = { targetOrgId, callback };

    logger.info(
      { targetOrgId, callback, allParams: Object.fromEntries(searchParams.entries()) },
      "Parsed query params",
    );

    if (!targetOrgId) {
      setError(t("common:settings.organization.switch.error-missing-id"));
      logger.error("No targetOrgId provided to switch-organization route", {
        ionRouterSearch: ionRouter.routeInfo.search,
        pathname: ionRouter.routeInfo.pathname,
      });

      // Redirect to home after 3 seconds
      setTimeout(() => {
        history.replace(Routes.App());
      }, 3000);
      return;
    }

    const performSwitch = async () => {
      try {
        logger.info({ targetOrgId, callback }, "Starting organization switch");

        // Call the switch API
        await api.api.switchOrganization(
          { organizationId: targetOrgId },
          { credentials: "include" },
        );

        logger.info({ targetOrgId }, "Organization switched successfully");

        // Wait for animation to be visible
        await new Promise((resolve) => setTimeout(resolve, 800));

        // Navigate to callback route using hard reload
        // This ensures all queries and router state are reset with the new organization context
        const targetRoute = callback || Routes.App();
        logger.info({ targetRoute }, "Reloading to target route");

        // Use window.location.href to force a full page reload
        window.location.href = targetRoute;
      } catch (err) {
        logger.error({ error: err }, "Failed to switch organization");
        setError(t("common:settings.organization.switch.error"));

        // After 3 seconds, navigate to home
        setTimeout(() => {
          history.replace(Routes.App());
        }, 3000);
      }
    };

    performSwitch();
  }, [ionRouter.routeInfo.search, history, sessionProvider.session.data?.user.organizationId]);

  // Use ref values for rendering to prevent them becoming undefined during reload
  const { targetOrgId, callback } = queryParamsRef.current;

  // Find organizations for display
  const organizations = organizationsData?.organizations ?? [];
  const currentOrgId = organizationsData?.activeOrganizationId;
  const currentOrg = organizations.find((org) => org.id === currentOrgId);
  const targetOrg = organizations.find((org) => org.id === targetOrgId);

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="flex h-full flex-col items-center justify-center gap-8 px-4">
          {error ? (
            <>
              {/* Error State */}
              <div className="rounded-full bg-error-10 p-6">
                <svg
                  className="size-12 text-error-120"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <div className="text-center">
                <h1 className="mb-2 text-2xl font-bold text-txt-primary">{error}</h1>
                <p className="text-txt-secondary">
                  {t("common:settings.organization.switch.redirecting")}
                </p>
              </div>
            </>
          ) : (
            <>
              {/* Switching Animation */}
              <div className="relative flex items-center gap-4">
                {/* Current Org Logo */}
                {currentOrg?.slug ? (
                  <SignedQueryImage
                    width={80}
                    height={80}
                    alt={currentOrg.name ?? "Current Organization"}
                    className="size-20 rounded-2xl object-cover"
                    {...queryOptions_signed_key_public_by_slug([
                      "android-chrome-512x512.png",
                      { slug: currentOrg.slug },
                    ])}
                  />
                ) : (
                  <div className="flex size-20 items-center justify-center rounded-2xl bg-brand-100">
                    <div className="size-12 rounded-full bg-brand-600" />
                  </div>
                )}

                {/* Arrow with Animation */}
                <div className="animate-pulse">
                  <ArrowRightLeft className="size-8 text-brand-600" />
                </div>

                {/* Target Org Logo */}
                {targetOrg?.slug ? (
                  <SignedQueryImage
                    width={80}
                    height={80}
                    alt={targetOrg.name ?? "Target Organization"}
                    className="size-20 rounded-2xl object-cover"
                    {...queryOptions_signed_key_public_by_slug([
                      "android-chrome-512x512.png",
                      { slug: targetOrg.slug },
                    ])}
                  />
                ) : (
                  <div className="flex size-20 items-center justify-center rounded-2xl bg-success-10">
                    <div className="size-12 rounded-full bg-success-100" />
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="text-center">
                <h1 className="mb-2 text-2xl font-bold text-txt-primary">
                  {t("common:settings.organization.switch.switching-title")}
                </h1>
                <p className="text-txt-secondary">
                  {t("common:settings.organization.switch.switching-description")}
                </p>
              </div>

              {/* Spinner */}
              <IonSpinner name="crescent" className="text-brand-600" />
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
